<template>
  <validation-observer
    v-slot="observer"
    class="form-plain-step"
    slim
  >
    <validation-observer-event-emitter
      :observer="observer"
      @update="updateObserver"
    >
      <section
        class="form-plain-step__section"
        :data-test="`step_${stepId}`"
      >
        <h2
          v-if="!isConfirmSteps"
          class="form-plain-step__title"
        >
          {{ title }}
        </h2>

        <form-plain-step-questions
          v-if="isAvailable"
          ref="steps"
          class="form-plain-step__questions"
          :data-test="`step_${stepId}_questions`"
          :session-data="sessionData"
          :questions="questions"
          :payload="payload"
          :step-id="stepId"
          :form-observer="formObserver"
          :validated="isStepValidated"
          :is-continue-triggered="isContinueTriggered"
          @data="$emit('data', $event)"
          @question-observer="onQuestionObserver"
          @render-after="setIsForcedShowContinue"
          @multiform-questions-details="multiformQuestionsDetails = $event"
          @edit-process="handleEditMultiform"
          @reset-continue-trigger="$emit('reset-continue-trigger', $event)"
        />
      </section>

      <step-errors
        v-if="isAvailable && (observer.failed || isStepValidated)"
        :id="`form-errors-${stepId}`"
        :step-ref="$refs.steps"
        :questions="questions"
        :component-key="`step${index+1}`"
        :multiform-questions-details="multiformQuestionsDetails"
        :question-observers="questionObservers"
        :step-id="stepId"
        class="mt-6"
        slim
      />

      <div
        v-show="isAvailable && !isNextStepAvailable && showContinue"
        class="form-plain-step__actions text-right pa-4"
      >
        <v-btn
          color="success"
          rounded
          x-large
          data-test="continue_btn"
          class="form-plain-step__actions--next lg-next-section-btn"
          @click.prevent="nextStep(observer)"
        >
          {{ continueBtnLabel }}
          <v-icon
            dark
            right
            size="18"
          >
            mdi-arrow-right
          </v-icon>
        </v-btn>
      </div>
    </validation-observer-event-emitter>
  </validation-observer>
</template>

<script>
import {
  flatten,
  isPlainObject,
} from 'lodash';
import { mapActions } from 'vuex';
import FormPlainStepQuestions from './FormPlainStepQuestions';
import { validate as veeValidate } from 'vee-validate';

export default {
  name: 'form-plain-step',
  components: {
    FormPlainStepQuestions,
  },
  inject: [
    'formMetadata',
    'helpers',
    'productName',
    'config',
  ],
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    sessionData: {
      type: Object,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    questions: {
      type: Array,
      required: true,
    },
    stepId: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isAvailable: {
      type: Boolean,
      required: true,
    },
    isNextStepAvailable: {
      type: Boolean,
      required: true,
    },
    isConfirmSteps: {
      type: Boolean,
      required: true,
    },
    isEditProcess: {
      type: Boolean,
      required: false,
    },
    isContinueTriggered: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      stepObserver: null,
      isStepValidated: false,
      questionObservers: {},
      /*
      * used for cases when we have multiple vehicle type (e.g. courier, gap)
      * we have multiple conditions for some questions like vehicleCost/vehiclePrice
      * when they should be rendered initially for specific vehicle type (button "continue to next step" shown)
      * and hidden for other types that uses vehicleSelector until user select and confirm vehicle (button "continue to next step" hidden until user confirm vehicle)
      * */
      isForcedShowContinue: false,
      multiformQuestionsDetails: [],
      isMultiformInEdit: false,
    };
  },
  computed: {
    isLastStep () { return this.index + 1 === this.formMetadata.steps.length; },
    continueBtnLabel () {
      if (this.isConfirmSteps) {
        return 'Continue';
      }

      const nextStep = this.formMetadata.steps[this.index + 1];
      return nextStep ? `Continue to ${nextStep.stepLabel}` : 'Continue';
    },
    mandatoryFields () {
      return Array.from(new Set(flatten(
        (() => {
          const fields = [];

          this.questions.forEach(({ params }) => {
            if (!params.renderAfter) return;

            if (Array.isArray(params.renderAfter)) fields.push(...params.renderAfter);
            if (isPlainObject(params.renderAfter)) fields.push(...Object.keys(params.renderAfter));
          });

          return fields;
        })(),
      )));
    },
    showContinue () {
      if (this.isLastStep) return false;

      if (this.isForcedShowContinue) return true;

      if (this.mandatoryFields.some(field => [undefined, null, ''].includes(this.payload[field]))) {
        return false;
      }

      return true;
    },
  },
  methods: {
    ...mapActions(['showToast']),
    $setStepValidated (value = true) { this.isStepValidated = value; },
    onQuestionObserver ({ questionId, observer }) {
      this.$set(this.questionObservers, questionId, observer);
    },
    setIsForcedShowContinue (val) {
      // Get value from Base if it has renderAfter conditions and receive isRenderAfterFullfilled
      this.isForcedShowContinue = val;
    },
    async nextStep (observer) {
      this.isStepValidated = true;
      const isValid = await observer.validate();

      if (!isValid) {
        await this.$nextTick();
        this.showToast({
          type: 'warning',
          text: 'Please fix all errors to continue',
        });
        this.$scrollTo(`#form-errors-${this.stepId}`);
        return;
      }

      // if multiform in edit process, inside multiformController re-run validation and if form is valid save data
      // after that allow user go further
      if (this.isEditProcess) {
        this.isMultiformInEdit = true;
        this.$emit('reset-continue-trigger', true);
        await this.nextStep(observer);
        return;
      }

      // Used for redirection to different products from confirm step depending on selected options on this step
      if (this.isConfirmSteps) {
        for (const redirect of this.formMetadata.redirects) {
          const isValidDirection = await this.validateRules(redirect.rules);

          if (isValidDirection) {
            // TODO refactor [Pavlo Lytvyn] Remove condition and 'window.location = redirect.url' after updates LG products redirects
            // https://seopa01.atlassian.net/browse/SU-4703
            if (this.config?.redirect_urls && redirect.productName) {
              window.location = this.config.redirect_urls[redirect.productName];
              return;
            }

            window.location = redirect.url;
            return;
          }
        };

        this.$emit('show-confirm-form', false);
        this.$scrollTo('#app');
        return;
      }

      this.$emit('passed', this.stepId);

      const nextStep = this.formMetadata.steps[this.index + 1];

      if (nextStep) {
        this.$scrollTo(`#form-plain-step-${nextStep.stepId}`);
      }
    },
    async validateRules (showRules) {
      const validity = await Promise.all(
        Object.keys(showRules).map(field => {
          const fieldRules = showRules[field];
          const value = this.payload[field];

          return veeValidate(value, fieldRules);
        }),
      );

      return !validity.some(({ valid }) => valid === false);
    },
    handleEditMultiform (e) {
      this.isMultiformInEdit = e;
      this.$emit('edit-process', e);
    },
    updateObserver ($event) {
      const observer = this.isMultiformInEdit && this.isContinueTriggered ? { ...$event, failed: true } : $event;
      this.stepObserver = observer;
      this.$emit('observer', observer);
    },
  },
};
</script>

<style lang="scss" scoped>
.form-plain-step {
  --padding-side: #{_percentage(100px, 934px)};

  &__section {
    border-radius: 6px;
    background: $clr-white;
    overflow: hidden;
    border: 1px solid $clr-heather;
  }

  &__title {
    color: var(--v-primary-base, $clr-primary);
    font-size: 24px;
    line-height: 1.2;
    font-weight: bold;
    text-transform: uppercase;
    padding: 7px calc(var(--padding-side) / 2);

    & + * {
      border-top: 1px solid #ccc;
    }
  }

  &__questions { padding: 1px 0; }

  &__actions {
    &--next {
      width: 100%;
    }
  }
}

@media screen and (min-width: 601px) {
  .form-plain-step {
    &__actions {
      &--next {
        width: auto;
      }
    }
  }
}

@media screen and (min-width: 768px) {
  .form-plain-step {
    &__title {
      padding: 15px var(--padding-side);
    }
  }
}
</style>
